import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ModalContextType {
  deleteModalOpen: boolean;
  conversationToDelete: string | null;
  openDeleteModal: (conversationId: string) => void;
  closeDeleteModal: () => void;
  renameModalOpen: boolean;
  conversationToRename: string | null;
  conversationTitle: string | null;
  openRenameModal: (conversationId: string, title: string) => void;
  closeRenameModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<
    string | null
  >(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [conversationToRename, setConversationToRename] = useState<
    string | null
  >(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );

  const openDeleteModal = (conversationId: string) => {
    setConversationToDelete(conversationId);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setConversationToDelete(null);
  };

  const openRenameModal = (conversationId: string, title: string) => {
    setConversationToRename(conversationId);
    setConversationTitle(title);
    setRenameModalOpen(true);
  };

  const closeRenameModal = () => {
    setRenameModalOpen(false);
    setConversationToRename(null);
    setConversationTitle(null);
  };

  return (
    <ModalContext.Provider
      value={{
        deleteModalOpen,
        conversationToDelete,
        openDeleteModal,
        closeDeleteModal,
        renameModalOpen,
        conversationToRename,
        conversationTitle,
        openRenameModal,
        closeRenameModal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};
